import {
  IDatasetLensColumn,
  IReportCoaDimensionMetadata,
  IReportConfiguration,
  IReportDimensionColumn
} from '@opengov/reporting-types';
import {
  createDimensionalReportColumn,
  createReportColumnMetaData,
  createReportColumnMetaDataTags,
  getColumnName,
  IDimensionalReportColumn,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';

export const convertCoaColsToDimCols = (
  coaColumns: IDatasetLensColumn[],
  joinColName: string,
  accountTypeMap: Record<string, string>,
  coaDimensionId: string
): IDimensionalReportColumn[] => {
  return coaColumns.map((col) => {
    const accountName = accountTypeMap[getColumnName(col.name)];
    return createDimensionalReportColumn({
      name: col.name,
      displayName: accountName ? `${col.label} (${accountName.substring(0, 2)})` : col.label,
      joinColumnName: joinColName,
      dimensionId: coaDimensionId,
      hidden: !col.visible,
      metadata: createReportColumnMetaData({ tags: createReportColumnMetaDataTags({ hidden: !col.visible }) }),
      semanticTypes: col.semanticTypes
    });
  });
};

// given the metadata structure for a single COA, this function
// returns an array indicating the order in which those columns
// should appear in the detail table
export const getColumnOrderFromCoaMetadata = (coaMetadata: IReportCoaDimensionMetadata): string[] => {
  const { segments, ledgerTypes = [] } = coaMetadata;
  const order = [];
  segments.concat(ledgerTypes).forEach((segmentOrLedgerType) => {
    order.push(segmentOrLedgerType.accountCodeColumnRef, segmentOrLedgerType.descriptionColumnRef);
    segmentOrLedgerType.categoryLevels.forEach((catLevel) => order.push(catLevel.columnRef));
  });
  return order;
};

export const addCoAColsAfterJoinCol = (
  lensColumns: IReportColumn[],
  coaColumns: IDimensionalReportColumn[],
  joinColName: string
): IReportColumn[] => {
  let joinColIndex: number;
  lensColumns.forEach((column, index) => {
    if (column.name === joinColName) {
      joinColIndex = index;
      return;
    }
  });

  // Only add the COA columns the configuration does not already carry. A configuration can hold a
  // pre-bound `{join}:{segment}` column — it survives every lens-based cleanup because compound names
  // are deliberately skipped there — and `selectCoaDimensionColumnsFn` generates that same compound
  // name, so adding it unconditionally aliases one column twice in the SELECT and query-service
  // rejects the whole statement with OGQL 806 ("non unique alias found"). Keeping the stored column
  // leaves it in the position the user put it, matching `addNewlyAddedColumnsToDetailsTable`.
  const existingColumnNames = new Set(lensColumns.map((column) => column.name));
  const columnsToAdd = coaColumns.filter((column) => !existingColumnNames.has(column.name));

  return lensColumns.slice(0, joinColIndex + 1).concat(columnsToAdd, lensColumns.slice(joinColIndex + 1));
};

export const addCoAColsToLensCols = ({
  reportConfig,
  coaMetadata,
  coaColumns,
  joinColName,
  accountTypeMap,
  coaDimensionId
}: {
  reportConfig: IReportConfiguration;
  coaMetadata: IReportCoaDimensionMetadata;
  coaColumns: IReportDimensionColumn[];
  joinColName: string;
  accountTypeMap: Record<string, string>;
  coaDimensionId: string;
}): IReportColumn[] => {
  if (!reportConfig) {
    return [];
  }
  const lensColumns = reportConfig.detailTableQuery.columns;
  if (!joinColName) {
    return lensColumns;
  }

  let coaColRecord = [];

  if (coaColumns) {
    const visibleColumns = coaColumns.filter((col) => col.visible);
    const order = coaMetadata ? getColumnOrderFromCoaMetadata(coaMetadata) : [];
    const unorderedCols = visibleColumns.filter((col) => order.indexOf(col.name) === -1);
    const orderedCols = visibleColumns.filter((col) => order.indexOf(col.name) !== -1);
    orderedCols.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
    const coaColsInOrder = unorderedCols.concat(orderedCols);
    coaColRecord = coaColRecord.concat(
      convertCoaColsToDimCols(coaColsInOrder, joinColName, accountTypeMap, coaDimensionId)
    );
  }

  return addCoAColsAfterJoinCol(lensColumns, coaColRecord, joinColName);
};
